"use client";

import {JSX} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useTranslation} from "@/shared/i18n";
import {ExpandableText} from "@/shared/ui/data-display/expandable-text";
import {type ContactPhoneItem, PhoneNumber} from "@/shared/ui/data-display/phone-number/phone-number";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/styles.scss";

/**
 * Owner photo data
 */
export interface OwnerPhoto {
    url: string;
    alt: string;
}

/**
 * Owner data for UI display
 */
export interface OwnerData {
    fullName: string;
    position: string;
    photo: OwnerPhoto;
    description: string;
    tenure: string;
}

export type OwnedProps = {
    title?: string;
    owner: OwnerData;
    onReadMoreClick?: () => void;
    contactPhones?: Array<ContactPhoneItem>;
};

export function Owned({title, owner, onReadMoreClick, contactPhones}: OwnedProps): JSX.Element {
    const {fullName, photo, position, description} = owner;
    const photoUrl = photo.url;
    const photoAlt = photo.alt || fullName;
    const {t} = useTranslation();
    return (
        <div className="owned">
            <Typography as="h2" size={16} className="owned__title">
                {title}
            </Typography>
            <div className="owned__card">
                <div className="owned__card-image">
                    <SmartImage src={photoUrl} alt={photoAlt} width={100} height={100} />
                </div>
                <div className="owned__card-name">
                    <Typography as="p" size={14} className="owned__card-name-title">
                        {fullName}
                    </Typography>
                    <Typography as="p" size={12} className="owned__card-name-position">
                        {position}
                    </Typography>
                </div>
            </div>
            <div className="owned__content">
                <div className="owned__content-main">
                    <ExpandableText
                        maxLines={{desktop: 2, mobile: 2, tablet: 2}}
                        charCount={description?.length}
                        ssrEstimation={{
                            desktop: {width: 513, fontSize: 14},
                            tablet: {width: 513, fontSize: 14},
                            mobile: {width: 420, fontSize: 14},
                        }}
                    >
                        <Typography as="div" size={14} weight={400} color="paragraph" className="welcome__description" innerHTML={description} />
                    </ExpandableText>
                </div>
                {(onReadMoreClick || (contactPhones && contactPhones.length > 0)) && (
                    <div className="owned__content-bottom">
                        {onReadMoreClick && (
                            <Typography as="div" size={12} className="owned__more" options={["underline"]} onClick={onReadMoreClick}>
                                {t("common.readMore")}
                            </Typography>
                        )}
                        {contactPhones && contactPhones.length > 0 && (
                            <div className="owned__phones">
                                {contactPhones.map((item, index) => (
                                    <PhoneNumber
                                        key={index}
                                        number={item.number}
                                        label={undefined}
                                        showLabel={false}
                                        iconSize={16}
                                        size={12}
                                        className="owned__phone-item"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <SmartImage src={assetPath("/images/destinations/locally-owned.svg")} className="owned__badge" alt={title ?? ""} width={67} height={67} />
        </div>
    );
}
